import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { SITE } from '@/lib/site'

interface SeoProps {
  title: string
  description: string
  /** Route path, e.g. '/' or '/book-a-demo'. Resolved to an absolute canonical URL. */
  canonical: string
  image?: string
  ogType?: 'website' | 'article'
  noindex?: boolean
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

export function Seo({
  title,
  description,
  canonical,
  image,
  ogType = 'website',
  noindex = false,
  jsonLd,
}: SeoProps) {
  const path = canonical.startsWith('/') ? canonical : `/${canonical}`
  const url = `${SITE.baseUrl}${path}`
  const ogImage = image ?? SITE.defaultOgImage
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  // Signals to the prerender crawler that the route has fully rendered. In dev StrictMode
  // this mounts→cleans→mounts; harmless because the prerender runs against a production build.
  useEffect(() => {
    document.documentElement.dataset['prerenderReady'] = 'true'
    return () => {
      delete document.documentElement.dataset['prerenderReady']
    }
  }, [])

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,follow" />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={SITE.ogImageAlt} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={SITE.locale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={SITE.ogImageAlt} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block).replace(/</g, '\\u003c')}
        </script>
      ))}
    </Helmet>
  )
}
